import React from "react";
import { useAuth } from "../../hooks/useAuth";
import { Button, Stack, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";

import { IStats } from "../../shared/types";
import { MINUS, formatCurrency, formatSignedCurrency } from "../../utils/formatters";
import DetailItem from "../../components/cards/subcomponents/DetailItem";
import { getCapitalFlowBreakdown } from "../../utils/capitalFlow";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { SectionHeader } from "../../components/ui/SectionHeader";

interface Props {
  stats: IStats;
  decimals: number;
  onOpenMovementsDialog: () => void;
}

const CapitalFlowCard: React.FC<Props> = ({
  stats,
  decimals,
  onOpenMovementsDialog,
}) => {
  const theme = useTheme();
  const { groups } = useAuth();
  const isAdmin = groups?.includes("admin");
  const {
    capitalBasis,
    capitalChange,
    extraBonusYield,
    netCapitalChange,
  } = getCapitalFlowBreakdown(stats);
  const hasExtraBonusYield = extraBonusYield !== 0;
  const pnlColor =
    netCapitalChange >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const capitalChangeColor =
    capitalChange >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const formattedPnl = formatSignedCurrency(netCapitalChange);

  return (
    <SurfaceCard sx={{ height: "100%" }}>
      <SectionHeader
        title="Capital flow"
        icon={SwapVertRoundedIcon}
        action={
          isAdmin && (
            <Button size="small" variant="outlined" color="inherit" onClick={onOpenMovementsDialog}>
              View movements
            </Button>
          )
        }
      />
      <Stack spacing={0.25}>
        <DetailItem
          label="Initial capital"
          value={formatCurrency(stats.initialCapital)}
        />
        <DetailItem
          label="Deposits"
          value={`+${formatCurrency(stats.depositsUSD)}`}
        />
        <DetailItem
          label="Withdrawals"
          value={stats.withdrawalsUSD > 0 ? `${MINUS}${formatCurrency(stats.withdrawalsUSD)}` : formatCurrency(0)}
        />
        <Divider sx={{ my: 0.75 }} />
        <DetailItem
          label="Total capital"
          value={formatCurrency(stats.totalCapitalUSD)}
          emphasis
        />
        {hasExtraBonusYield && (
          <DetailItem
            label="P&L before premiums"
            value={formatSignedCurrency(capitalChange)}
            valueColor={capitalChangeColor}
            sections={[
              {
                title: "P&L before premiums",
                items: [
                  {
                    label: "Capital basis",
                    value: formatCurrency(capitalBasis),
                  },
                  {
                    label: "Formula",
                    value: "Total capital − basis",
                  },
                ],
              },
            ]}
          />
        )}
        {hasExtraBonusYield && (
          <DetailItem
            label="Premiums"
            value={formatSignedCurrency(extraBonusYield)}
            valueColor={
              extraBonusYield >= 0
                ? theme.palette.success.main
                : theme.palette.error.main
            }
          />
        )}
        <DetailItem
          label="Total P&L"
          value={formattedPnl}
          valueColor={pnlColor}
          emphasis
          sections={[
            {
              title: "Total P&L",
              items: [
                {
                  label: "P&L before premiums",
                  value: formatSignedCurrency(capitalChange),
                },
                {
                  label: "Premiums",
                  value: formatSignedCurrency(extraBonusYield),
                },
              ],
            },
          ]}
        />
      </Stack>
    </SurfaceCard>
  );
};

export default CapitalFlowCard;
